import { useMemo, useState, useCallback } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Calendar as CalendarIcon,
  ThumbsUp,
  MessageCircle,
  Link as LinkIcon,
  Image as ImageIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { FBPost } from "@/types/fb-type";
import { MediaPreview } from "./media-preview";

type PostListProps = {
  posts: FBPost[];
  selectedId?: string | null;
  defaultSelectedId?: string | null;
  onSelect?: (post: FBPost) => void;
  isLoading?: boolean;
  className?: string;
  height?: number | string;
  showSearch?: boolean;
  fmtDateTime?: (iso: string) => string;
};

export function PostList({
  posts,
  selectedId,
  defaultSelectedId = null,
  onSelect,
  showSearch = true,
  fmtDateTime,
  isLoading,
}: PostListProps) {
  const [internalSelected, setInternalSelected] = useState<string | null>(
    defaultSelectedId
  );
  const [q, setQ] = useState("");
  const selected = selectedId ?? internalSelected;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return posts;
    return posts.filter(
      (p) =>
        p.message?.toLowerCase().includes(t) ||
        p.statusType?.toLowerCase().includes(t) ||
        p.fromName?.toLowerCase().includes(t)
    );
  }, [posts, q]);

  const handleSelect = useCallback(
    (p: FBPost) => {
      if (selectedId === undefined) setInternalSelected(p.id);
      onSelect?.(p);
    },
    [onSelect, selectedId]
  );

  const formatDate = useCallback(
    (iso: string) => {
      if (fmtDateTime) return fmtDateTime(iso);
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    [fmtDateTime]
  );

  return (
    <div>
      {/* Header / Toolbar */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100 sticky top-0 bg-white z-10 shrink-0">
        <div className="text-sm font-semibold text-slate-800">Posts</div>
        {showSearch && (
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search"
            className="h-8 w-64 text-sm"
          />
        )}
      </div>

      {/* Scrollable list */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="divide-y divide-slate-100">
          {isLoading && (
            <div className="animate-pulse p-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-3 py-3">
                  <div className="h-12 w-12 rounded-md bg-slate-200"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-slate-200"></div>
                    <div className="h-3 w-2/3 rounded bg-slate-200"></div>
                    <div className="h-3 w-1/2 rounded bg-slate-200"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading &&
            filtered.map((post) => {
              const isSelected = selected === post.id;
              return (
                <button
                  key={post.id}
                  type="button"
                  onClick={() => handleSelect(post)}
                  className={cn(
                    "group w-full text-left p-3 transition-colors focus:outline-none",
                    isSelected ? "bg-indigo-50" : "hover:bg-slate-50"
                  )}
                  aria-pressed={isSelected}
                >
                  <div className="flex items-start gap-3">
                    <MediaPreview mediaUrl={post.mediaUrl} mediaType={post.mediaType} thumbnail={post.thumbnailUrl} />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="line-clamp-2 text-sm font-medium text-slate-900">
                            {post.message || post.story || "(no message)"}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                            {post.statusType && (
                              <Badge
                                variant="secondary"
                                className="h-5 px-2 text-[10px]"
                              >
                                {post.statusType}
                              </Badge>
                            )}
                            {post.fromName && (
                              <span className="truncate">
                                by {post.fromName}
                              </span>
                            )}
                          </div>
                        </div>

                        {post.permalinkUrl && (
                          <a
                            href={post.permalinkUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
                          >
                            <LinkIcon className="h-3.5 w-3.5" />
                            Open
                          </a>
                        )}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center gap-1">
                            <ThumbsUp className="h-4 w-4" />
                            {post.totalReactions}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <MessageCircle className="h-4 w-4" />
                            {post.totalComments} Comments
                          </span>
                          <Badge
                            variant="outline"
                            className="h-5 px-2 text-[10px]"
                          >
                            Shares: {post.shares}
                          </Badge>
                        </div>
                        <div className="inline-flex items-center gap-1 text-slate-500">
                          <CalendarIcon className="h-4 w-4" />
                          {formatDate(post.createdTime)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={cn(
                      "mt-2 h-0.5 w-full rounded transition-colors",
                      isSelected
                        ? "bg-indigo-400"
                        : "bg-transparent group-hover:bg-slate-200"
                    )}
                  />
                </button>
              );
            })}

          {!isLoading && filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No posts found.
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
